import {
  ImmutableAPIAccessTrapError,
  ImmutableAPIServiceError,
  NotDefinedInSchemaError,
  SchemaNotDefinedError,
  UnknownAPIEndpointError
} from '../errors';
import { type Method, type SchemaData, type Service } from '../types';
import { type EndpointsContainer } from './EndpointsContainer';

enum SchemaStatuses {
  WaitingForSchema = 'WAITING_FOR_SCHEMA',
  SchemaDefined = 'SCHEMA_DEFINED'
}

export class APISchema {
  private readonly schema = new Set();
  private schemaStatus = SchemaStatuses.WaitingForSchema;
  private container: EndpointsContainer;

  constructor(container: EndpointsContainer) {
    this.container = container;
  }

  define(schemaData: SchemaData) {
    if (this.schemaStatus !== SchemaStatuses.WaitingForSchema) {
      return;
    }

    for (const endpoint in schemaData) {
      this.schema.add(endpoint);
      this.container.set(endpoint);
    }

    this.schemaStatus = SchemaStatuses.SchemaDefined;
  }

  throwIfErrors(key: string) {
    if (this.schemaStatus !== SchemaStatuses.SchemaDefined) {
      throw new SchemaNotDefinedError();
    }

    if (!this.schema.has(key)) {
      throw new NotDefinedInSchemaError(key);
    }
  }
}

export class InternalAccessManager {
  private readonly container: EndpointsContainer;
  apiSchema: APISchema;

  constructor(container: EndpointsContainer) {
    this.container = container;
    this.apiSchema = new APISchema(container);
  }

  registerService(name: string, service: Service): void {
    this.apiSchema.throwIfErrors(name);

    const existingService = this.container.get(name);

    if (!existingService) {
      this.container.set(name, service);
    } else {
      // APIAccessTrap throws an error upon set attempt
      try {
        Object.assign(existingService, service);
      } catch (error) {
        if (error instanceof ImmutableAPIAccessTrapError) {
          this.container.set(name, service);
        }
      }
    }
  }

  registerMethod(name: string, method: Method): void {
    this.apiSchema.throwIfErrors(name);
    this.container.set(name, method);
  }

  unregisterEndpoint(name: string): void {
    this.container.delete(name);
  }
}

export class PublicAccessManager {
  get(container: EndpointsContainer, key: string) {
    const endpoint = container.get(key);

    if (!endpoint) {
      throw new UnknownAPIEndpointError(key);
    }

    return endpoint;
  }

  set(_container: EndpointsContainer, prop: string): never {
    throw new ImmutableAPIServiceError(prop);
  }
}
